// Benepack OMS · Topbar 审批提醒铃铛 (go-live 反馈 #3 · 三面之一)
//   Badge(待审总数) + BellOutlined → 点开下拉列待审单据 (类型/单号/对手方/金额) · 各行跳详情/审批页 ·
//   底部「查看审批中心」跳 /procurement/approvals。
//   count/items 来自共享 usePendingApprovals (Provider 单轮询 · 已 RBAC 门控 · 无权限用户 count=0)。
//
// 截断守卫: hook.truncated → 徽标显 "≥N" (Badge count 是 number · 用 string 不行 → 截断时给一个
//   带 "+" 语义的展示: AntD Badge 支持 count={node} 自定义 → 用 <span> 显 "≥N")。
import { useMemo } from 'react';
import { Badge, Button, Dropdown, Empty, List, Space, Typography } from 'antd';
import { BellOutlined, FileTextOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RefName from '@/components/RefName';
import { usePendingApprovals, type PendingApprovalItem } from '@/hooks/usePendingApprovals';
import LinkCell from '@/components/LinkCell';

const { Text } = Typography;

/** 类型 → 图标 + i18n label key (下拉每行左侧)。 */
const TYPE_META: Record<
  PendingApprovalItem['type'],
  { icon: React.ReactNode; labelKey: string; refKind: 'supplier' | 'customer' }
> = {
  purchase_order: {
    icon: <ShoppingCartOutlined style={{ color: '#e8590c' }} />,
    labelKey: 'approvalReminder.typePurchaseOrder',
    refKind: 'supplier',
  },
  quotation: {
    icon: <FileTextOutlined style={{ color: '#4A6FAF' }} />,
    labelKey: 'approvalReminder.typeQuotation',
    refKind: 'customer',
  },
};

export function ApprovalBell() {
  const { t } = useTranslation(['common']);
  const nav = useNavigate();
  const { count, items, truncated } = usePendingApprovals();

  // 下拉面板 (列表 + 底部跳审批中心) · 用 dropdownRender 注入富内容 (非 menu items)。
  const panel = useMemo(
    () => (
      <div
        style={{
          width: 360,
          maxHeight: 420,
          overflowY: 'auto',
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          border: '1px solid #f0f0f0',
        }}
      >
        <div
          style={{
            padding: '10px 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text strong>{t('approvalReminder.bellTitle')}</Text>
          {count > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`${truncated ? t('approvalReminder.approxPrefix') : ''}${count} ${t(
                'approvalReminder.countSuffix',
              )}`}
            </Text>
          )}
        </div>
        {count === 0 ? (
          <div style={{ padding: '24px 16px' }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('approvalReminder.empty')} />
          </div>
        ) : (
          <List<PendingApprovalItem>
            size="small"
            dataSource={items}
            rowKey={(it) => `${it.type}-${it.id}`}
            renderItem={(it) => {
              const meta = TYPE_META[it.type];
              return (
                <List.Item
                  style={{ padding: '10px 16px', cursor: 'pointer' }}
                  onClick={() => nav(it.to)}
                >
                  <List.Item.Meta
                    avatar={<span style={{ fontSize: 16 }}>{meta.icon}</span>}
                    title={
                      <Space size={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t(meta.labelKey)}
                        </Text>
                        <Text strong>{it.no}</Text>
                      </Space>
                    }
                    description={
                      <Space size={12} style={{ fontSize: 12 }}>
                        <RefName kind={meta.refKind} id={it.counterpartyId} />
                        <Text type="secondary">{`${it.currency} ${it.amount}`}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center',
          }}
        >
          <LinkCell to="/procurement/approvals">{t('approvalReminder.viewAll')}</LinkCell>
        </div>
      </div>
    ),
    [t, nav, count, items, truncated],
  );

  // Badge count: 截断时显 "≥N" (自定义 node) · 否则数字 (0 自动隐藏)。
  const badgeCount =
    truncated && count > 0 ? (
      <span
        style={{
          background: '#ff4d4f',
          color: '#fff',
          fontSize: 12,
          lineHeight: '16px',
          padding: '0 6px',
          borderRadius: 8,
          display: 'inline-block',
        }}
      >
        {`${t('approvalReminder.approxPrefix')}${count}`}
      </span>
    ) : (
      count
    );

  return (
    // P2-5 (UI-2): 触发器用真 Button (可 Tab 聚焦/回车展开) · 替代裸 icon cursor:pointer
    <Dropdown trigger={['click']} placement="bottomRight" dropdownRender={() => panel}>
      <Button
        type="text"
        aria-label={t('approvalReminder.bellTitle')}
        style={{ height: 'auto', padding: '4px 8px' }}
      >
        <Badge count={badgeCount} size="small" overflowCount={99}>
          <BellOutlined style={{ fontSize: 18 }} />
        </Badge>
      </Button>
    </Dropdown>
  );
}
